/**
 * Maps backend retry/skip denial codes to user-facing messages.
 *
 * Backend denial codes come from retryPolicyService and monthlyBudgetPolicyService.
 * Each code maps to a concise, actionable message that tells the user
 * what happened and what they can do about it.
 */

export interface RetryDenialPresentation {
  title: string;
  body: string;
  /** Whether the user should be prompted to upgrade their plan. */
  showUpgrade: boolean;
}

type UserPlan = 'free' | 'pro' | 'enterprise' | undefined;

interface DenialEntry {
  title: string;
  /** Body text. If a function, receives the user plan for plan-aware messaging. */
  body: string | ((plan: UserPlan) => string);
  /** Whether upgrade applies. If a function, receives the user plan. */
  showUpgrade: boolean | ((plan: UserPlan) => boolean);
}

/** Whether this plan should see self-serve upgrade language. */
function canSelfUpgrade(plan: UserPlan): boolean {
  return plan === 'free' || plan === undefined;
}

/** Build body text for limit-reached denials with plan awareness. */
function limitBody(freeMsg: string, enterpriseMsg: string): (plan: UserPlan) => string {
  return (plan) => {
    if (plan === 'enterprise') return enterpriseMsg;
    return freeMsg;
  };
}

const DENIAL_MAP: Record<string, DenialEntry> = {
  // ─── Retry policy denials ──────────────────────
  PLAN_RETRY_NOT_ALLOWED: {
    title: 'Retries not available',
    body: limitBody(
      'Your current plan does not include retries. Upgrade to unlock this feature.',
      'Retries are not enabled for your organization. Contact your admin.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },
  NODE_MAX_RETRIES_REACHED: {
    title: 'Retry limit reached',
    body: 'This step has used all of its available retries.',
    showUpgrade: false,
  },
  RETRY_LIMIT_REACHED: {
    title: 'Run retry limit reached',
    body: 'This query has used all available retries. Try a new query instead.',
    showUpgrade: false,
  },
  RETRY_COST_BUDGET_EXCEEDED: {
    title: 'Retry budget reached',
    body: 'The retry cost budget for this query has been reached.',
    showUpgrade: false,
  },
  RETRY_TOKEN_BUDGET_EXCEEDED: {
    title: 'Retry budget reached',
    body: 'The retry token budget for this query has been reached.',
    showUpgrade: false,
  },
  MONTHLY_RETRY_LIMIT_REACHED: {
    title: 'Monthly retries exhausted',
    body: limitBody(
      'You\'ve used all your retries for this month. Upgrade for more.',
      'Your organization has used all retries for this month. Contact your admin.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },
  NODE_NOT_RETRYABLE: {
    title: 'Cannot retry',
    body: 'This step type does not support retries.',
    showUpgrade: false,
  },

  // ─── Monthly budget denials ────────────────────
  MONTHLY_MODEL_TOKEN_BUDGET_EXCEEDED: {
    title: 'Monthly AI limit reached',
    body: limitBody(
      'You\'ve reached your monthly AI usage limit. Upgrade to Pro for unlimited access.',
      'Your organization has reached its monthly AI usage limit. Contact your admin to adjust capacity.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },
  MONTHLY_EMBEDDING_TOKEN_BUDGET_EXCEEDED: {
    title: 'Monthly processing limit reached',
    body: limitBody(
      'You\'ve reached your monthly document processing limit. Upgrade to Pro for unlimited access.',
      'Your organization has reached its monthly document processing limit. Contact your admin.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },
  MONTHLY_COST_BUDGET_EXCEEDED: {
    title: 'Monthly usage limit reached',
    body: limitBody(
      'You\'ve reached your monthly usage limit. Upgrade to Pro for unlimited access.',
      'Your organization has reached its monthly usage limit. Contact your admin to adjust capacity.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },
  MONTHLY_AI_CONVERSATIONS_EXCEEDED: {
    title: 'Monthly conversations used',
    body: limitBody(
      'You\'ve used all your AI conversations this month. Upgrade to Pro for unlimited access.',
      'Your organization has used all AI conversations this month. Contact your admin.',
    ),
    showUpgrade: (plan) => canSelfUpgrade(plan),
  },

  // ─── State/validation denials ──────────────────
  INVALID_STATE: {
    title: 'Cannot retry',
    body: 'This step is not in a state that allows retrying.',
    showUpgrade: false,
  },
  NOT_RETRYABLE: {
    title: 'Cannot retry',
    body: 'This step type does not support retries.',
    showUpgrade: false,
  },
  MAX_RETRIES: {
    title: 'Retry limit reached',
    body: 'This step has used all of its available retries.',
    showUpgrade: false,
  },
  NOT_SKIPPABLE: {
    title: 'Cannot skip',
    body: 'This step cannot be skipped.',
    showUpgrade: false,
  },
};

const DEFAULT_DENIAL: RetryDenialPresentation = {
  title: 'Action not available',
  body: 'This action cannot be completed right now. Please try again later.',
  showUpgrade: false,
};

function resolve(entry: DenialEntry, plan: UserPlan): RetryDenialPresentation {
  return {
    title: entry.title,
    body: typeof entry.body === 'function' ? entry.body(plan) : entry.body,
    showUpgrade: typeof entry.showUpgrade === 'function' ? entry.showUpgrade(plan) : entry.showUpgrade,
  };
}

/**
 * Map a backend denial code to a user-facing presentation.
 * Falls back to a generic message for unknown codes.
 *
 * @param code - Backend denial code
 * @param userPlan - Optional user plan for plan-aware messaging
 */
export function getRetryDenialPresentation(code: string, userPlan?: UserPlan): RetryDenialPresentation {
  const entry = DENIAL_MAP[code];
  if (!entry) return DEFAULT_DENIAL;
  return resolve(entry, userPlan);
}

/**
 * Map a backend denial code to a single-line user message.
 * Useful for inline feedback where title+body is too verbose.
 */
export function getRetryDenialMessage(code: string, userPlan?: UserPlan): string {
  const presentation = getRetryDenialPresentation(code, userPlan);
  return presentation.body;
}
